import type { ComponentProps, MouseEventHandler } from 'react'
import { memo, useCallback } from 'react'

import type { ItemCardVariant } from '../types'

import {
  tripSavedItemBookNowClicked,
  tripSavedItemOfferDetailsClicked,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { DetailItemCardVariant } from 'tripPlanner/components/DetailItemCard/common/types'
import useTripPlannerCTALabels from 'tripPlanner/hooks/useTripPlannerCTALabels'
import useRestoreCartButtonProps from 'tripPlanner/hooks/useRestoreCartButtonProps'
import type { LETripItem } from 'tripPlanner/types/tripItem'
import { getIsLuxPlusExclusiveAccess } from 'tripPlanner/utils/luxPlux'
import { addTripIdParam } from 'tripPlanner/utils/url'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'

interface Props {
  item: LETripItem
  className?: string
  variant: ItemCardVariant | DetailItemCardVariant
}

function LEOfferCTAs({ item, className, variant }: Props) {
  const canEdit = useCanEditTrip()
  const { searchFlightsCopy, viewOfferCopy } = useTripPlannerCTALabels()

  const { tripId } = item

  const { canRestoreCart, onClick, label, productLink } =
    useRestoreCartButtonProps(item)

  const isLuxPlusExclusiveAccess = getIsLuxPlusExclusiveAccess(item)

  const source =
    variant === 'drawer' ? 'trip_drawer_all_saved_items' : 'trip_summary'
  const modalElement = useModalElementContext()
  const onClickViewButton = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(() => {
    fireInteractionEvent(tripSavedItemOfferDetailsClicked(source))
    modalElement.resolve()
  }, [modalElement, source])

  // analytics event now fires unconditionally when this handler runs which is fine since its only wired to 'Book now' button
  const onClickRestoreCartButton = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      fireInteractionEvent(tripSavedItemBookNowClicked(source))
      onClick(e)
    },
    [source, onClick],
  )

  const viewCopy =
    item.type === 'FLIGHT' ? searchFlightsCopy : viewOfferCopy

  let viewOfferButtonKind: ComponentProps<typeof TextButton>['kind'] =
    'secondary'
  if (isLuxPlusExclusiveAccess) {
    viewOfferButtonKind = 'primary'
  } else if (item.type === 'FLIGHT' && variant === 'drawer') {
    viewOfferButtonKind = 'primary'
  } else if (canEdit && canRestoreCart && variant === 'drawer') {
    viewOfferButtonKind = 'tertiary'
  }
  const restoreCartButtonKind: ComponentProps<typeof TextButton>['kind'] =
    variant === 'drawer' ? 'primary' : 'secondary'

  const buttonSize = variant === 'mapSummary' ? 'small' : 'medium'

  return (
    <ProductPaletteProvider
      palette={isLuxPlusExclusiveAccess ? 'lux-plus' : 'default'}
    >
      <Group
        direction="horizontal"
        className={className}
        verticalAlign="center"
        horizontalAlign="stretch"
        tabletHorizontalAlign="space-between"
        gap={12}
      >
        {productLink && <TextButton
          kind={viewOfferButtonKind}
          to={addTripIdParam(productLink, tripId, canEdit)}
          target="_blank"
          onClick={onClickViewButton}
          size={buttonSize}
        >
          {viewCopy}
        </TextButton>}
        {canRestoreCart && variant === 'drawer' && (
          <TextButton
            kind={restoreCartButtonKind}
            onClick={onClickRestoreCartButton}
            size={buttonSize}
          >
            {label}
          </TextButton>
        )}
      </Group>
    </ProductPaletteProvider>
  )
}

export default memo(LEOfferCTAs)
